import type {LitigationLedgerAccess} from './litigationLedger.js';

/** Reserved identity for the ledger administrator's own organization unit. */
export const LITIGATION_CREATOR_UNIT_ID = '__ledger_creator_unit__';
export type LitigationCollaborationSettings = {
  createdBy?: string;
  collaborationUnits?: Array<{id: string}>;
  teamEditors?: Array<{userId: string; unitId: string}>;
};

/** Never infer unit identity from names, abbreviations, codes, or empty values. */
export function litigationCollaborationUnitForUser(settings: LitigationCollaborationSettings, userId: string, workbookCreatedBy = ''): string {
  if (!userId) return '';
  if (userId === workbookCreatedBy || userId === settings.createdBy) return LITIGATION_CREATOR_UNIT_ID;
  const unitId = settings.teamEditors?.find(item => item.userId === userId)?.unitId || '';
  return unitId === LITIGATION_CREATOR_UNIT_ID || settings.collaborationUnits?.some(unit => unit.id === unitId) ? unitId : '';
}

export function litigationUnitAccess(settings: LitigationCollaborationSettings, userId: string, workbookCreatedBy: string): {canViewAll: boolean; editableOwnerUserIds: string[]} {
  const unit = litigationCollaborationUnitForUser(settings, userId, workbookCreatedBy);
  const owners = new Set([userId]);
  if (unit) {
    for (const candidate of [workbookCreatedBy, settings.createdBy || '', ...(settings.teamEditors || []).map(editor => editor.userId)]) {
      if (candidate && litigationCollaborationUnitForUser(settings, candidate, workbookCreatedBy) === unit) owners.add(candidate);
    }
  }
  return {canViewAll: unit === LITIGATION_CREATOR_UNIT_ID, editableOwnerUserIds: [...owners].filter(Boolean)};
}

/** Access IDs are issued by the server. Missing metadata retains the old own-row rule. */
export function canEditLitigationCaseForAccess(access: LitigationLedgerAccess, ownerUserId: string): boolean {
  return Boolean(access.canCreate && ownerUserId && (ownerUserId === access.currentUserId || access.editableOwnerUserIds?.includes(ownerUserId)));
}

export function canDeleteLitigationCaseForAccess(access: LitigationLedgerAccess, ownerUserId: string): boolean {
  return access.isAdmin || canEditLitigationCaseForAccess(access, ownerUserId);
}
